import { useId } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { KTSVG, toAbsoluteUrl } from '@helpers/index';
import { Locale, setLanguage, useLang } from '@shared/i18n/Metronici18n';
import { LOCALE_ENDONYMS, LOCALE_FLAGS, orderLocales } from '@shared/i18n/localeOptions';

/**
 * The language picker as it appears inside the profile dropdown.
 *
 * Separate from `shared/i18n/LanguageSwitcher` rather than a variant of it because
 * the two are the same choice in two different shells: the `/profile` one is a
 * standalone card with a heading and an explanatory paragraph, this one has to
 * pass for a row of the surrounding menu. It borrows the menu's own
 * `profile-dropdown-menu-content-item` class so the flag, the label and the
 * padding line up with the links above it — sharing markup with the card would
 * mean parameterising every one of those classes. What the two genuinely share —
 * the names, the flags and the ordering rule — lives in `localeOptions`.
 *
 * Deliberately NOT collapsed into `PROFILE_MENU_ITEMS`: those entries are all
 * navigation, one icon plus one destination, and a language is neither. Adding it
 * there would mean a special case inside the map for something that is a choice,
 * not a link.
 *
 * Lives next to `UserProfileDropdown` rather than in `shared/i18n` because it is
 * coupled to that component's stylesheet, and colocating makes the coupling
 * visible to whoever edits either one.
 */
export const ProfileLanguageOptions = () => {
  const intl = useIntl();
  const activeLocale = useLang();
  // The visible label doubles as the group's accessible name via `aria-labelledby`.
  // An `aria-label` alongside it would give the group two names and read the text
  // out twice.
  const labelId = useId();

  // setLanguage persists to localStorage and reloads the page — the reload is how
  // the tree picks up the new locale, since MetronicI18nProvider reads storage once
  // on mount rather than subscribing to it.
  //
  // Re-selecting the active language is a no-op rather than a reload, so the option
  // stays tappable without costing a pointless page load. The dropdown still closes
  // either way: the wrapper in UserProfileDropdown toggles on any click inside it,
  // which is the same thing that happens when you tap one of the links.
  const handleSelect = (locale: Locale) => {
    if (locale === activeLocale) return;
    setLanguage(locale);
  };

  return (
    <div className="profile-dropdown-menu-content-languages">
      <span className="profile-dropdown-menu-content-languages-label" id={labelId}>
        {intl.formatMessage({ id: 'SETTINGS.LANGUAGE.ARIA_GROUP' })}
      </span>

      <div role="radiogroup" aria-labelledby={labelId}>
        {orderLocales(activeLocale).map((locale) => {
          const isActive = locale === activeLocale;

          return (
            <button
              key={locale}
              type="button"
              role="radio"
              aria-checked={isActive}
              // Marks the endonym as being in its own language, so a screen reader
              // says "Français" in French rather than reading it as German.
              lang={locale}
              onClick={() => handleSelect(locale)}
              className={clsx('profile-dropdown-menu-content-item', {
                'profile-dropdown-menu-content-item-active': isActive,
              })}>
              <img
                src={toAbsoluteUrl(LOCALE_FLAGS[locale])}
                alt=""
                aria-hidden="true"
                className="profile-dropdown-menu-content-flag"
              />
              <span className="profile-dropdown-menu-content-text">{LOCALE_ENDONYMS[locale]}</span>
              {isActive && (
                <KTSVG
                  path="/media/svg/general/checkmark.svg"
                  className="profile-dropdown-menu-content-check"
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
